<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useRoute } from "vue-router";
import { useAcademyStore, useListStore, useUserStore } from "@/stores";

import {
  BaseKit,
  Bold,
  Color,
  Fullscreen,
  Heading,
  Highlight,
  History,
  Image,
  Italic,
  Link,
  Strike,
  Table,
  Underline,
  Video,
  VuetifyTiptap,
} from "vuetify-pro-tiptap";
import "vuetify-pro-tiptap/style.css";

const extensions = [
  BaseKit.configure({
    placeholder: {
      placeholder: "Enter some text...",
    },
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color,
  Highlight,
  Heading,
  Link,
  Image,
  Video,
  Table,
  Fullscreen,
  History,
];

const route = useRoute();
const userStore = useUserStore();
const listStore = useListStore();
const academyStore = useAcademyStore();

const user = userStore.user;

const courses = computed(() => listStore.courses);

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  if (isValid) {
    academyStore.createStudentEvaluation(data);
  }
};

const rules = {
  studentId: {
    required: helpers.withMessage("Student ID is required", required),
  },
  tutor: {
    required: helpers.withMessage("Tutor is required", required),
  },
  beneficiary: {
    required: helpers.withMessage("Student is required", required),
  },
  course: {
    required: helpers.withMessage("Course is required", required),
  },
};

const searchBeneficiaries = ref(null);
const beneficiaries = ref([]);

const isLoading = ref(false);

const onInput = async (val) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    beneficiaries.value = await listStore.fetchBeneficiaryIntakes({
      keyword: val,
    });
    isLoading.value = false;
  } else {
    beneficiaries.value = [];
  }
};

const form = ref({
  studentId: null,
  beneficiary: null,
  tutor: null,
  course: null,
  commitment: null,
  involvement: null,
  consistency: null,
  languageSkills: null,
  selfConfidence: null,
  suitability: null,
  effort: null,
  improvement: null,
  workReady: null,
  workReadySpecify: null,
  comments: null,
});

const evaluation = ref({});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$dirty && v$.value[field].$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const tickLabels = (options) => {
  const ticks = [];
  options.forEach((element) => {
    ticks.push(element.name);
  });
  return ticks;
};

onMounted(async () => {
  if (route.name === "tutor-evaluation") {
    const data = await academyStore.getEvaluation("tutor");
    evaluation.value = data;
  }
  if (route.name === "student-evaluation") {
    const data = await academyStore.getEvaluation("student");
    evaluation.value = data;
  }
  await listStore.fetchCourses();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="6">
        <h1 class="text-academy">Add Student Evaluation</h1>
      </v-col>
      <v-col cols="12" sm="6" class="d-flex justify-start justify-sm-end align-center">
        <v-btn color="academy" size="large" @click="submitForm">
          <v-icon icon="mdi-plus" /> Create student evaluation
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-text-field
            v-model="form.studentId"
            name="id"
            label="Student Evaluation (ID)"
            hide-details="auto"
            :error-messages="getErrorMessages('studentId').value"
            @blur="v$.studentId.$touch()"
          >
            <template #label>
              Student Evaluation (ID)
              <span v-if="isRequired('studentId')" class="ml-1 red--text">
                <strong> *</strong>
              </span>
            </template>
          </v-text-field>
          <v-text-field
            v-model="form.tutor"
            name="tutor"
            label="Tutor name"
            hide-details="auto"
            :error-messages="getErrorMessages('tutor').value"
            @blur="v$.tutor.$touch()"
          >
            <template #label>
              Tutor Name
              <span v-if="isRequired('tutor')" class="ml-1 red--text">
                <strong> *</strong>
              </span>
            </template>
          </v-text-field>

          <v-autocomplete
            v-model="form.beneficiary"
            v-model:search-input="searchBeneficiaries"
            :loading="isLoading"
            return-object
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="beneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            :error-messages="getErrorMessages('beneficiary').value"
            @update:search="(val) => onInput(val)"
            @blur="v$.beneficiary.$touch()"
          >
            <template #label>
              Student Name
              <span v-if="isRequired('beneficiary')" class="ml-1 red--text">
                <strong> *</strong>
              </span>
            </template>
          </v-autocomplete>

          <Autocomplete
            v-model="form.course"
            label="Course Completed"
            :items="courses"
            :multiple="false"
            :chips="false"
            :blur-check="() => v$.course.$touch()"
            :error-messages="getErrorMessages('course').value"
            :is-required="isRequired('course')"
          />

          <div v-for="question in evaluation.questions" :key="question.id">
            <p v-if="question.type === 'SCALE'" class="font-weight-bold">
              {{ question.name }}
            </p>
            <v-slider
              v-if="question.type === 'SCALE'"
              v-model="form[question.key]"
              :max="question.numOfOptions"
              :min="1"
              :step="1"
              :ticks="tickLabels(question.options)"
              color="academy"
              thumb-label
              show-ticks="always"
            />

            <v-radio-group
              v-if="question.type === 'CHOICE'"
              v-model="form[question.key]"
              class="custom-radio-group"
              color="academy"
              hide-details="auto"
            >
              <v-radio
                v-for="option in question.options"
                :key="option.id"
                :value="option.id"
                :label="option.name"
              />
              <template #label>
                <span class="font-weight-bold">{{ question.name }}</span>
              </template>
            </v-radio-group>

            <VuetifyTiptap
              v-if="question.type === 'TEXT'"
              v-model="form[question.key]"
              :label="question.name"
              rounded
              :min-height="200"
              :extensions="extensions"
            />
          </div>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-academy));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
